import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { blue, red, yellow } from '@mui/material/colors'
import type { Theme } from '@mui/material/styles/createTheme'

type SxProp = string | ((theme: Theme) => string)

interface CountrySpecificProps {
  backgroundColor: SxProp
  borderColor: SxProp
  color: SxProp
  countryCode: string
}

const baseCountrySpecificProps: CountrySpecificProps = {
  backgroundColor: 'common.white',
  borderColor: 'common.black',
  color: 'common.black',
  countryCode: ''
}

const countrySpecificProps: Record<string, CountrySpecificProps | undefined> = {
  NL: {
    ...baseCountrySpecificProps,
    backgroundColor: yellow[700],
    countryCode: 'NL'
  },
  BE: {
    ...baseCountrySpecificProps,
    borderColor: red[900],
    color: red[900],
    countryCode: 'B'
  },
  FR: {
    ...baseCountrySpecificProps,
    countryCode: 'F'
  },
  DE: {
    ...baseCountrySpecificProps,
    countryCode: 'D'
  },
  GB: {
    ...baseCountrySpecificProps,
    countryCode: 'GB'
  },
  IT: {
    ...baseCountrySpecificProps,
    countryCode: 'I'
  },
  CZ: {
    ...baseCountrySpecificProps,
    countryCode: 'CZ'
  }
}

function queryCountrySpecificProps(country: string): CountrySpecificProps {
  return countrySpecificProps[country] ?? baseCountrySpecificProps
}

interface Props {
  children: string
}

export function LicensePlate(props: Props) {
  const [country, license] = props.children.split('|')

  const countrySpecificProps = queryCountrySpecificProps(country)

  const licensePlateHeight = 32
  const borderSize = 2
  const blueSquareSize = licensePlateHeight - 2 * borderSize // box height minus top and bottom border size so it's square.

  return (
    <Box
      sx={{
        width: 144,
        height: licensePlateHeight,
        backgroundColor: countrySpecificProps.backgroundColor,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 1,
        overflow: 'hidden',
        border: borderSize,
        borderColor: countrySpecificProps.borderColor
      }}
    >
      <Box
        sx={{
          width: blueSquareSize,
          backgroundColor: blue[900],
          // 100% instead of a static height.
          // using a static height causes pixel rounding issues.
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography color='common.white' fontSize={12}>
          {countrySpecificProps.countryCode}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1
        }}
      >
        <Typography color={countrySpecificProps.color} fontWeight='bold'>
          {license}
        </Typography>
      </Box>
    </Box>
  )
}
